import { useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

/**
 * Небольшой алерт снизу, который появляется если похоже, что у пользователя
 * включен VPN / прокси. Работает на эвристике: сравниваем таймзону браузера
 * с таймзоной/страной, определённой по IP.
 *
 * Используем несколько fallback-сервисов на случай, если один недоступен.
 */

// Провайдеры с CORS, бесплатные без ключа.
const PROVIDERS = [
  {
    name: "ipapi.co",
    url: "https://ipapi.co/json/",
    parse: (d) => ({
      timezone: d?.timezone || "",
      country: d?.country_code || d?.country || "",
      org: (d?.org || "").toLowerCase(),
    }),
  },
  {
    name: "geojs",
    url: "https://get.geojs.io/v1/ip/geo.json",
    parse: (d) => {
      const first = Array.isArray(d) ? d[0] : d;
      return {
        timezone: first?.timezone || "",
        country: first?.country_code || "",
        org: (first?.organization || "").toLowerCase(),
      };
    },
  },
  {
    name: "ipinfo.io",
    url: "https://ipinfo.io/json",
    parse: (d) => ({
      timezone: d?.timezone || "",
      country: d?.country || "",
      org: (d?.org || "").toLowerCase(),
    }),
  },
];

async function fetchWithTimeout(url, ms = 4000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

async function detectVpn() {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (!browserTz) return false;

  for (const p of PROVIDERS) {
    const raw = await fetchWithTimeout(p.url);
    if (!raw) continue;

    const info = p.parse(raw);
    if (!info.timezone && !info.org) continue;

    // 1) Сравнение таймзон по континенту
    if (info.timezone) {
      const browserRegion = browserTz.split("/")[0];
      const ipRegion = info.timezone.split("/")[0];
      if (browserRegion && ipRegion && browserRegion !== ipRegion) {
        return true;
      }
    }

    // 2) Провайдер похож на хостинг/VPN/датацентр
    if (info.org) {
      const suspicious = [
        "vpn", "proxy", "hosting", "datacenter", "data center",
        "ovh", "hetzner", "digitalocean", "linode", "vultr",
        "amazon", "aws", "google cloud", "azure",
      ];
      if (suspicious.some((s) => info.org.includes(s))) {
        return true;
      }
    }

    // Удачный ответ — выходим, не дёргаем остальные
    return false;
  }

  return false;
}

export default function VpnAlert() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("vpn_alert_shown") === "1") return;

    let cancelled = false;
    let hideTimer = null;

    const run = async () => {
      const isVpn = await detectVpn();
      if (cancelled) return;
      if (isVpn) {
        setVisible(true);
        sessionStorage.setItem("vpn_alert_shown", "1");
        hideTimer = setTimeout(() => setVisible(false), 10000);
      }
    };

    const startTimer = setTimeout(run, 1500);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const handleClose = () => setVisible(false);

  return (
    <AnimatePresence>
      {visible && (
        <Box
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            sx={{
                position: "fixed",
                left: 0,
                bottom: 0,
                transform: "translateX(-50%)",
                zIndex: 20000,
                // maxWidth: "92vw",
                // width: "100dvh",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.75,
                py: 1.25,
                // pr: 1,
                // borderRadius: "14px",
                // bgcolor: "rgba(23,23,23,0.96)",
                // color: "#fff",
                // boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                // backdropFilter: "blur(8px)",
                // border: "1px solid rgba(255,255,255,0.08)",
            }}
        >
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                sx={{
                    transform: "translateX(-50%)",
                    zIndex: 20000,
                    // maxWidth: "92vw",
                    // width: "90dvh",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.75,
                    py: 1.25,
                    borderRadius: "14px",
                    bgcolor: "rgba(23,23,23,0.96)",
                    color: "#fff",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.08)",
                }}
            >
            <Box
                sx={{
                    // width: 32, height: 32, flexShrink: 0,
                    p: 1,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,180,60,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#ffb43c",
                }}
            >
                <WarningAmberRoundedIcon sx={{ fontSize: 20, color: "#ffb43c" }} />
            </Box>
            <Typography
                sx={{
                    fontSize: 13, lineHeight: 1.35,
                    color: "#fff", maxWidth: 320,
                }}
            >
                Похоже, у вас включён VPN. Загрузка фотографий и сохранение книги
                могут работать медленнее обычного.
            </Typography>
            <IconButton
                onClick={handleClose}
                size="sm"
                variant="plain"
                sx={{
                    color: "rgba(255,255,255,0.7)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" },
                }}
            >
                <CloseRoundedIcon sx={{ fontSize: 18, color: 'white' }} />
            </IconButton>
            </Box>
        </Box>
      )}
    </AnimatePresence>
  );
}